//============================================================================
// Name        : queue.ts
// Purpose     : Adjustment Requests (Tenant Admin) - the queue: row details,
//               selection (this page / all pages), Reject, Recheck & Apply,
//               Resume
// Author      : Senitron Team
// Copyright   : Copyright 2026 Senitron Corporation. All rights reserved.
//============================================================================
import { busy, errorMessage, IDict, idle, IPageData, post, toast } from "./common";
import { Modals } from "./modals";

export class Queue {
  // Bulk scope: the ticked rows, or every open request matching the filters (all pages).
  private allMatching = false;

  constructor(private root: HTMLElement, private data: IPageData, private modals: Modals,
              private t: (key: string, vars?: IDict) => string) {
    this.bindRows();
    this.bindSelection();
    this.bindReject();
    this.bindApply();
    this.bindResume();
    this.refreshSelection();
  }

  // ---------------------------------------------------------- row details
  // Rows open their details by click, Enter or Space (keyboard users too).
  private bindRows(): void {
    const toggle = (row: HTMLElement) => {
      const detail = this.root.querySelector('[data-detail-for="' + row.getAttribute("data-id") + '"]');
      if (!detail) { return; }
      const open = !detail.classList.toggle("hidden");
      row.setAttribute("aria-expanded", open ? "true" : "false");
    };
    this.root.addEventListener("click", (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const row = target.closest(".ar-row") as HTMLElement;
      if (row && !target.closest(".col-check, a, button, input")) { toggle(row); }
    });
    this.root.addEventListener("keydown", (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (!target.classList.contains("ar-row") || (event.key !== "Enter" && event.key !== " ")) { return; }
      event.preventDefault();
      toggle(target);
    });
  }

  // ---------------------------------------------------------- selection
  private checks(): HTMLInputElement[] {
    return Array.prototype.slice.call(this.root.querySelectorAll(".ar-check:not([disabled])"));
  }

  private selectedIds(): string[] {
    return this.checks().filter((box) => box.checked).map((box) => box.value);
  }

  private selectedCount(): number {
    return this.allMatching ? this.data.totalMatching : this.selectedIds().length;
  }

  private refreshSelection(): void {
    const bar = document.getElementById("ar-selection");
    if (!bar) { return; }
    const count = this.selectedCount();
    document.getElementById("ar-selected-text").textContent = this.t(this.allMatching ? "selected_all" : "selected", { count });
    bar.classList.toggle("hidden", count === 0);
  }

  private bindSelection(): void {
    const all = document.getElementById("ar-select-all") as HTMLInputElement;
    this.root.addEventListener("change", (event: Event) => {
      if (!(event.target as HTMLElement).classList.contains("ar-check")) { return; }
      this.allMatching = false;
      this.refreshSelection();
    });
    if (all) {
      all.addEventListener("change", () => {
        this.checks().forEach((box) => { box.checked = all.checked; });
        this.allMatching = false;
        this.refreshSelection();
        if (all.checked && this.data.totalMatching > this.selectedIds().length) { this.modals.open("ar-scope-modal", all); }
      });
    }
    const pick = (allPages: boolean) => () => {
      this.allMatching = allPages;
      this.modals.close("ar-scope-modal");
      this.refreshSelection();
    };
    this.on("ar-scope-page", pick(false));
    this.on("ar-scope-all", pick(true));
    this.on("ar-clear", () => {
      this.allMatching = false;
      this.checks().forEach((box) => { box.checked = false; });
      if (all) { all.checked = false; }
      this.refreshSelection();
    });
  }

  // The selection as form fields (Reject) or request data (Recheck, Apply).
  private selectionData(): IDict {
    return this.allMatching ? $.extend({ all_matching: "1" }, this.data.matchingParams) : { ids: this.selectedIds() };
  }

  private fillForm(form: HTMLFormElement): void {
    form.querySelectorAll("input.ar-selection-param").forEach((input: Element) => input.remove());
    const add = (name: string, value: string) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      input.className = "ar-selection-param";
      form.appendChild(input);
    };
    const data = this.selectionData();
    Object.keys(data).forEach((name) => {
      if (Array.isArray(data[name])) { data[name].forEach((value: string) => add(name + "[]", value)); } else { add(name, data[name]); }
    });
  }

  // ---------------------------------------------------------- reject
  private bindReject(): void {
    this.on("ar-reject", (button) => {
      this.fillForm(document.getElementById("ar-reject-form") as HTMLFormElement);
      document.getElementById("ar-reject-title").textContent = this.t("reject_title", { count: this.selectedCount() });
      this.modals.open("ar-reject-modal", button);
    });
  }

  // ---------------------------------------------------------- recheck & apply
  private bindApply(): void {
    const form = document.getElementById("ar-apply-form") as HTMLFormElement;
    const confirm = document.getElementById("ar-apply-confirm") as HTMLButtonElement;
    if (!form || !confirm) { return; }
    const body = this.root.querySelector("#ar-apply-modal .ar-recheck-body") as HTMLElement;

    this.on("ar-apply", (recheck) => {
      body.innerHTML = "";
      body.appendChild(this.message("p", "text-muted", this.t("rechecking")));
      confirm.disabled = true;
      this.modals.open("ar-apply-modal", recheck);
      busy(recheck as HTMLButtonElement);
      post(this.data.recheckUrl, this.selectionData())
        .done((response: IDict) => this.renderRecheck(body, confirm, response.rows || [], response.total || 0))
        .fail((xhr: any) => {
          body.innerHTML = "";
          body.appendChild(this.message("div", "banner banner--error", errorMessage(xhr, this.t("recheck_failed"))));
        })
        .always(() => idle(recheck as HTMLButtonElement));
    });

    // Applying runs in the background: the answer comes back as a toast and the
    // claimed rows show "Applying" without a reload.
    form.addEventListener("submit", (event: Event) => {
      event.preventDefault();
      if (confirm.disabled) { return; }
      const label = busy(confirm, this.t("applying"));
      post(form.action, this.selectionData())
        .done((response: IDict) => {
          toast(response.message, true);
          this.modals.close("ar-apply-modal");
          idle(confirm, label);
          this.markApplying(response.ids || []);
          this.watch(response.ids || []);
        })
        .fail((xhr: any) => {
          toast(errorMessage(xhr, this.t("failed")), false);
          idle(confirm, label);
        });
    });
  }

  private markApplying(ids: number[]): void {
    ids.forEach((id) => {
      const pill = this.root.querySelector('[data-status-for="' + id + '"]') as HTMLElement;
      if (pill) {
        pill.innerHTML = '<i class="fa fa-spinner fa-spin"></i> ';
        pill.appendChild(document.createTextNode(this.t("applying_status")));
        pill.className = "status-pill missing";
      }
      const box = this.root.querySelector('.ar-check[value="' + id + '"]') as HTMLInputElement;
      if (box) { box.remove(); }
    });
    this.allMatching = false;
    const all = document.getElementById("ar-select-all") as HTMLInputElement;
    if (all) { all.checked = false; }
    this.refreshSelection();
  }

  // Applying runs in the background: ask where the requests are every 2
  // seconds and update their rows in place; stop when all are finished (or
  // after 90 seconds, when no worker has picked them up) with a summary.
  private watch(ids: number[]): void {
    if (!ids.length || !this.data.progressUrl) { return; }
    const started = Date.now();
    const poll = () => {
      $.ajax({ url: this.data.progressUrl, data: { ids }, dataType: "json", timeout: 15000 })
        .done((response: IDict) => {
          const rows: IDict[] = response.rows || [];
          rows.filter((row) => row.done).forEach((row) => this.showResult(row));
          const waiting = rows.filter((row) => !row.done).length;
          if (waiting === 0) {
            const applied = rows.filter((row) => /applied/.test(row.status)).length;
            const failed = rows.filter((row) => row.status === "failed").length;
            toast(this.t("progress_done", { applied, failed }), true);
          } else if (Date.now() - started > 90000) {
            toast(this.t("progress_waiting", { count: waiting }), false);
          } else {
            window.setTimeout(poll, 2000);
          }
        })
        .fail(() => { if (Date.now() - started < 90000) { window.setTimeout(poll, 4000); } });
    };
    window.setTimeout(poll, 2000);
  }

  // A finished request: its pill says how it ended; applied rows fade out of
  // the waiting list (they now belong to the other tabs).
  private showResult(row: IDict): void {
    const pill = this.root.querySelector('[data-status-for="' + row.id + '"]') as HTMLElement;
    if (!pill || pill.getAttribute("data-final") === "1") { return; }
    pill.setAttribute("data-final", "1");
    pill.textContent = row.label;
    pill.className = "status-pill " + row.css;
    if (row.error) {
      const note = document.createElement("span");
      note.className = "cell-sub error";
      note.textContent = row.error;
      pill.insertAdjacentElement("afterend", note);
    }
    if (/applied/.test(row.status)) {
      const tr = pill.closest("tr");
      if (tr) { tr.classList.add("dimmed"); }
    }
  }

  private renderRecheck(body: HTMLElement, confirm: HTMLButtonElement, rows: IDict[], total: number): void {
    const moved = rows.filter((row) => row.moved).length;
    const problems = rows.filter((row) => row.problem).length;
    const stores = Object.keys(rows.reduce((acc: IDict, row) => { acc[row.store] = true; return acc; }, {})).length;
    const count = Math.max(total, rows.length);
    body.innerHTML = "";
    body.appendChild(this.message("p", "", this.t("across", { count: rows.length, stores })));
    if (total > rows.length) { body.appendChild(this.message("div", "banner banner--running", this.t("only_first", { shown: rows.length, total }))); }
    if (moved > 0) {
      body.appendChild(this.message("div", "banner banner--warn", this.t("moved", { count: moved })));
    } else if (rows.length - problems > 0) {
      body.appendChild(this.message("div", "banner", this.t("unchanged")));
    }
    if (problems > 0) { body.appendChild(this.message("div", "banner banner--error", this.t("problems", { count: problems }))); }
    const changed = rows.filter((row) => row.decision_changed && !row.eos_changes).length;
    if (changed > 0) { body.appendChild(this.message("div", "banner banner--running", this.t("changed", { count: changed }))); }
    body.appendChild(this.message("p", "text-muted", this.t("after_note")));

    const table = document.createElement("table");
    table.className = "data";
    const head = table.createTHead().insertRow();
    ["col_request", "col_location", "col_item", "col_eos", "col_submitted", "col_now", "col_after"].forEach((key) => {
      const th = document.createElement("th");
      th.textContent = this.t(key);
      head.appendChild(th);
    });
    const tbody = table.createTBody();
    rows.forEach((row) => {
      const tr = tbody.insertRow();
      if (row.moved) { tr.classList.add("tone-warn"); }
      if (row.problem) { tr.classList.add("tone-error"); }
      [row.request_number, row.store, row.item, row.external_system, row.rfid_qty_before + " vs " + row.ext_qty_before,
       row.problem ? row.problem : row.rfid_qty + " vs " + row.ext_qty, this.afterText(row)].forEach((value) => {
        tr.insertCell().textContent = value === null || value === undefined ? "" : String(value);
      });
    });
    body.appendChild(table);
    confirm.textContent = this.t("apply_count", { count });
    confirm.disabled = count === 0;
  }

  // "0 vs 0", or "3 vs 2 (Shopify unchanged)" when Senitron is ahead now.
  private afterText(row: IDict): string {
    if (row.problem || row.rfid_after === undefined) { return ""; }
    const text = row.rfid_after + " vs " + row.ext_after;
    return row.eos_changes ? text : text + " (" + this.t("eos_unchanged", { system: row.external_system }) + ")";
  }

  // ---------------------------------------------------------- resume
  private bindResume(): void {
    const form = document.getElementById("ar-resume-form") as HTMLFormElement;
    const button = document.getElementById("ar-resume") as HTMLButtonElement;
    if (!form || !button) { return; }
    form.addEventListener("submit", (event: Event) => {
      event.preventDefault();
      if (button.disabled) { return; }
      const label = busy(button, this.t("resuming"));
      post(form.action, {})
        .done((response: IDict) => {
          toast(response.message, true);
          const banner = document.getElementById("ar-stale-banner");
          if (banner) { banner.remove(); }
        })
        .fail((xhr: any) => {
          toast(errorMessage(xhr, this.t("failed")), false);
          idle(button, label);
        });
    });
  }

  // ---------------------------------------------------------- helpers
  private on(id: string, handler: (element: HTMLElement) => void): void {
    const element = document.getElementById(id);
    if (element) { element.addEventListener("click", () => { if (!(element as HTMLButtonElement).disabled) { handler(element); } }); }
  }

  private message(tag: string, className: string, text: string): HTMLElement {
    const element = document.createElement(tag);
    element.className = className;
    element.textContent = text;
    return element;
  }
}

declare var $: any;
